import dayjs from 'dayjs';
import { addDays, endOfWeek, format, startOfWeek, parseISO } from 'date-fns';
import { formatDuration, getWeekInterval } from './general.helpers';

export interface WeekDaySchedule {
  date: string;
  trainings: any[];
}

export type WeekSchedule = Record<string, WeekDaySchedule>;

export interface WeekStatsSplit {
  duration: string;
  distance: number;
  sessions: number;
}

export interface SplitWeekStats {
  draft: WeekStatsSplit;
  live: WeekStatsSplit;
  unpublishedIds: string[];
  unpublishedCount: number;
}

export const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const createEmptyWeekSchedule = (inputWeek: dayjs.Dayjs): WeekSchedule => {
  const start = addDays(startOfWeek(new Date(inputWeek.format('YYYY-MM-DD'))), 1);
  const end = addDays(endOfWeek(new Date(inputWeek.format('YYYY-MM-DD'))), 1);

  const interval = getWeekInterval(start, end);
  const week: WeekSchedule = {};
  interval.forEach((day) => {
    week[format(day, 'EEEE')] = {
      date: day.toISOString(),
      trainings: [],
    };
  });

  return week;
};

export const buildWeekScheduleFromTrainings = (inputWeek: dayjs.Dayjs, trainings: any[]): WeekSchedule => {
  const currWeek = createEmptyWeekSchedule(inputWeek);
  trainings.forEach((training) => {
    const dayName = format(parseISO(training.time), 'EEEE');
    if (currWeek[dayName]) {
      currWeek[dayName].trainings.push(training);
    }
  });
  return currWeek;
};

const isExcludedForGroup = (training: any, groupId?: string): boolean => {
  if (!groupId) return false;
  if (training.trainingType === 'team' && training.excludedGroupIds?.includes(groupId)) {
    return true;
  }
  return false;
};

const sumStats = (trainings: any[]): WeekStatsSplit => {
  let durationSeconds = 0;
  let distance = 0;
  trainings.forEach((training) => {
    durationSeconds += Number(training.duration) || 0;
    distance += Number(training.distance) || 0;
  });
  return {
    duration: formatDuration(durationSeconds.toString()),
    distance,
    sessions: trainings.length,
  };
};

export const calculateSplitWeekStats = (trainings: any[], groupId?: string): SplitWeekStats => {
  const filtered = trainings.filter((training) => !isExcludedForGroup(training, groupId));
  const draft = filtered.filter((training) => !training.published);
  const live = filtered.filter((training) => training.published);

  return {
    draft: sumStats(draft),
    live: sumStats(live),
    unpublishedIds: draft.map((training) => training.id),
    unpublishedCount: draft.length,
  };
};

export const getWeekDateRangeLabel = (week: dayjs.Dayjs): string => {
  const start = addDays(startOfWeek(new Date(week.format('YYYY-MM-DD'))), 1);
  const end = addDays(endOfWeek(new Date(week.format('YYYY-MM-DD'))), 1);
  const sameMonth = format(start, 'MMM') === format(end, 'MMM');
  if (sameMonth) {
    return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
  }
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
};

export const isSameCalendarDay = (isoDate: string, compare: Date = new Date()): boolean => {
  return format(parseISO(isoDate), 'yyyy-MM-dd') === format(compare, 'yyyy-MM-dd');
};
